import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CreateBerthInput,
  CreatePontoonInput,
  UpdateBerthInput,
  UpdatePontoonInput,
} from '@/lib/marina/validators'

export interface Berth {
  id: string
  code: string
  number: number
  pontoonId: string
  pontoon?: { id: string; code: string; name: string | null }
  maxLoaMeters: string
  maxBeamMeters: string
  maxDraftMeters: string
  depthMeters: string
  hasWater: boolean
  hasElectric: boolean
  amperage: number | null
  dailyRateIdr: string
  monthlyRateIdr: string | null
  yearlyRateIdr: string | null
  mapX: number | null
  mapY: number | null
  mapWidth: number | null
  mapHeight: number | null
  mapRotation: number
  status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'MAINTENANCE' | 'BLOCKED'
  notes: string | null
}

export interface Pontoon {
  id: string
  code: string
  name: string | null
  sortOrder: number
  _count?: { berths: number }
}

async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export function useBerths(params: { pontoonId?: string; status?: string } = {}) {
  const qs = new URLSearchParams()
  if (params.pontoonId) qs.set('pontoonId', params.pontoonId)
  if (params.status) qs.set('status', params.status)
  return useQuery({
    queryKey: ['berths', params],
    queryFn: () => apiFetch<{ items: Berth[]; total: number }>(`/api/berths${qs.toString() ? `?${qs}` : ''}`),
    staleTime: 30_000,
  })
}

export function usePontoons() {
  return useQuery({
    queryKey: ['pontoons'],
    queryFn: () => apiFetch<{ pontoons: Pontoon[] }>('/api/pontoons'),
    staleTime: 60_000,
  })
}

export function useBerth(id: string | null | undefined) {
  return useQuery({
    queryKey: ['berth', id],
    queryFn: () => apiFetch<{ berth: Berth }>(`/api/berths/${id}`),
    enabled: !!id,
  })
}

async function jsonFetch<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export function useCreatePontoon() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreatePontoonInput) =>
      jsonFetch<{ pontoon: Pontoon }>('/api/pontoons', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pontoons'] }),
  })
}

export function useUpdatePontoon() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePontoonInput }) =>
      jsonFetch<{ pontoon: Pontoon }>(`/api/pontoons/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pontoons'] }),
  })
}

export function useDeletePontoon() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<{ ok: boolean }>(`/api/pontoons/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pontoons'] })
      qc.invalidateQueries({ queryKey: ['berths'] })
    },
  })
}

export function useCreateBerth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateBerthInput) =>
      jsonFetch<{ berth: Berth }>('/api/berths', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['berths'] })
      qc.invalidateQueries({ queryKey: ['pontoons'] })
    },
  })
}

export function useUpdateBerth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBerthInput }) =>
      jsonFetch<{ berth: Berth }>(`/api/berths/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['berths'] })
      qc.invalidateQueries({ queryKey: ['berth', vars.id] })
    },
  })
}

export function useDeleteBerth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<{ ok: boolean }>(`/api/berths/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['berths'] })
      qc.invalidateQueries({ queryKey: ['pontoons'] })
    },
  })
}

export interface BerthPositionUpdate {
  id: string
  mapX: number
  mapY: number
  mapWidth?: number | null
  mapHeight?: number | null
  mapRotation?: number | null
}

export function useBulkBerthPosition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (updates: BerthPositionUpdate[]) =>
      jsonFetch<{ ok: boolean; updated: number }>('/api/berths/bulk-position', {
        method: 'POST',
        body: JSON.stringify(updates),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['berths'] }),
  })
}
